import interact from "interactjs";

import type { ISetTableDimensionCmd } from "@/data/commands/table/SetTableDimensionCmd";
import { Factory } from "@/data/dao/Factory";
import { TableDao } from "@/data/dao/service/TableDao";
import { RoomShape, TableShape } from "@/data/model/Enums";
import type { IRoomWithService } from "@/data/model/Room";
import type { ITable, ITableDimension } from "@/data/model/Table";
import { Format } from "@/data/util/Format";

export class FloorPlanner {
    private $element: JQuery;
    private $canvas: JQuery;
    private $unplanned: JQuery;

    private room: IRoomWithService;
    private tableDao: TableDao;

    constructor(room: IRoomWithService) {
        this.room = room;

        this.tableDao = Factory.getInstance(TableDao);
        this.$element = $('<div class="roomplanner" />');
        this.$canvas = $('<div class="canvas" />').appendTo(this.$element);
        this.$unplanned = $('<div class="unplanned" />').appendTo(this.$element);

        $(window).bind("resize.floorplaner", () => {
            this.onResize();
        });

        interact(this.$canvas[0]).dropzone({
            accept: ".ghosttable",
            ondrop: (event) => {
                const pos = event.dragEvent.page;
                const offset = this.$element.offset();
                const parOffset = this.$element.parent().offset();

                pos.x -= offset.left + parOffset.left;
                pos.y -= offset.top;

                if (pos.x < 0) pos.x = 0;

                if (pos.y < 0) pos.y = 0;

                this.onTableDroppedToCanvas($(event.relatedTarget), pos);
            },
            ondragenter: (event) => {
                $(event.relatedTarget).addClass("dropready");
            },
            ondragleave: (event) => {
                $(event.relatedTarget).removeClass("dropready");
            },
        });

        this.onResize();
        this.loadTables();
    }

    public onChangeShape(shape: RoomShape) {
        this.room.shape = shape;
        this.onResize();
    }

    public loadTables() {
        this.tableDao.getData({ roomId: this.room.id, limit: 150 }).then((tables) => {
            for (const i in tables.items) {
                const table = tables.items[i];
                if (table.dimension === undefined) this.addTableToUnplanned(table);
                else this.addTableToCanvas(table);
            }
        });
    }

    private addTableToUnplanned(table: ITable) {
        const unplannedTable = $(
            '<div class="unplanned">' +
                Format.htmlEscape(table.name) +
                '<div class="handle"><i class="fas fa-bars"></i></div></div>'
        )
            .data("model", table)
            .appendTo(this.$unplanned);

        interact(unplannedTable[0])
            .draggable({
                manualStart: true,
                listeners: {
                    move: (event) => {
                        const target = $(event.target);
                        const x = (parseFloat(target.attr("data-x")) || 0) + event.dx;
                        const y = (parseFloat(target.attr("data-y")) || 0) + event.dy;

                        target.css("transform", "translate(" + x + "px, " + y + "px)");
                        target.attr("data-x", x);
                        target.attr("data-y", y);
                    },
                    end: (e) => {
                        if (e.dropzone !== undefined) return;

                        $(e.target).remove();
                    },
                },
            })
            .on("move", (event) => {
                const interaction = event.interaction;

                if (interaction.pointerIsDown && !interaction.interacting()) {
                    const currentTarget = $(event.currentTarget);
                    const table: ITable = currentTarget.data("model");
                    const ghostTable = $('<div class="ghosttable">' + Format.htmlEscape(table.name) + "</div>")
                        .data("model", table)
                        .appendTo(this.$element);

                    const currentOffset = currentTarget.position();
                    const parentPos = currentTarget.parent().position();
                    const x = currentOffset.left + parentPos.left;
                    const y = currentOffset.top + parentPos.top;

                    ghostTable.css({
                        transform: "translate(" + x + "px, " + y + "px)",
                        "line-height": "80px",
                        height: "80px",
                        width: "120px",
                    });
                    ghostTable.attr("data-x", x);
                    ghostTable.attr("data-y", y);

                    interaction.start({ name: "drag" }, event.interactable, ghostTable[0]);
                }
            });
    }

    private addTableToCanvas(table: ITable) {
        const plannedTable = $(
            '<div class="table"><div class="inner"><span>' + Format.htmlEscape(table.name) + "<span></div></div>"
        )
            .data("model", table)
            .appendTo(this.$canvas);
        plannedTable.addClass(TableShape[table.shape].toLowerCase());

        $('<div class="remover"><i class="far fa-trash-alt"></i></div>')
            .appendTo(plannedTable)
            .click((e) => {
                e.preventDefault();
                this.onRemoveTableFromPlanning($(e.currentTarget).parent());
            });

        this.updateTableByDimension(plannedTable, this.minMaxDimension(table.dimension!));

        interact(plannedTable[0])
            .draggable({
                inertia: false,
                modifiers: [
                    interact.modifiers.restrictRect({
                        restriction: "parent",
                        endOnly: true,
                    }),
                ],
                listeners: {
                    move: (event) => {
                        const target = $(event.target);
                        const x = (parseFloat(target.attr("data-x")) || 0) + event.dx;
                        const y = (parseFloat(target.attr("data-y")) || 0) + event.dy;

                        target.css("transform", "translate(" + x + "px, " + y + "px)");
                        target.attr("data-x", x);
                        target.attr("data-y", y);
                    },
                    end: (event) => {
                        const target = $(event.target);
                        target.css("transform", "");

                        const newLeft = this.widthToPercent(
                            (parseFloat(target.attr("data-x")) || 0) + target.position().left
                        );
                        const newTop = this.heightToPercent(
                            (parseFloat(target.attr("data-y")) || 0) + target.position().top
                        );

                        target
                            .css({
                                left: newLeft + "%",
                                top: newTop + "%",
                            })
                            .removeAttr("data-x")
                            .removeAttr("data-y");

                        this.onTableDimensionChanged(target);
                    },
                },
                autoScroll: false,
            })
            .resizable({
                edges: { left: true, right: true, bottom: true, top: true },
                modifiers: [
                    interact.modifiers.restrictRect({
                        restriction: "parent",
                        endOnly: true,
                    }),
                ],
                listeners: {
                    move: (event) => {
                        const target = $(event.target);
                        const currentPos = target.position();

                        if (event.preEnd === true) return;

                        target.css({
                            height: this.heightToPercent(event.rect.height) + "%",
                            width: this.widthToPercent(event.rect.width) + "%",
                            top: this.heightToPercent(currentPos.top + event.deltaRect.top) + "%",
                            left: this.widthToPercent(currentPos.left + event.deltaRect.left) + "%",
                        });
                    },
                    end: (event) => {
                        this.onTableDimensionChanged($(event.target));
                    },
                },
            });
    }

    private updateTableByDimension($element: JQuery, dimension: ITableDimension) {
        $element.css({
            top: dimension.posY + "%",
            left: dimension.posX + "%",
            width: dimension.width + "%",
            height: dimension.height + "%",
        });
    }

    private onTableDroppedToCanvas($element: JQuery, atPosition: { x: number; y: number }) {
        const table: ITable = $element.data("model");
        this.tableDao
            .setPosition(
                table.id,
                this.minMaxDimension({
                    posX: this.widthToPercent(atPosition.x),
                    posY: this.heightToPercent(atPosition.y),
                    height: this.heightToPercent(80),
                    width: this.widthToPercent(80),
                })
            )
            .then((t) => {
                this.addTableToCanvas(t);
            });

        $element.remove();

        this.$unplanned.find("> .unplanned").each((i, e) => {
            const uE = $(e);
            if (uE.data("model").id === table.id) {
                uE.remove();
                return false;
            }
        });
    }

    private onTableDimensionChanged($element: JQuery) {
        const model: ITable = $element.data("model");
        interact($element.addClass("saving")[0]).draggable(false).resizable(false);

        const pos = $element.position();
        const newDimension: ISetTableDimensionCmd = this.minMaxDimension({
            width: this.widthToPercent($element.width()),
            height: this.heightToPercent($element.height()),
            posX: this.widthToPercent(pos.left),
            posY: this.heightToPercent(pos.top),
        });

        this.tableDao.setPosition(model.id, newDimension).then((newModel) => {
            interact($element.removeClass("saving")[0]).draggable(true).resizable(true);

            this.updateTableByDimension($element.data("model", newModel), newModel.dimension!);
        });
    }

    private onRemoveTableFromPlanning($element: JQuery) {
        const model: ITable = $element.data("model");
        this.tableDao.removePosition(model.id).then((newModel) => {
            this.addTableToUnplanned(newModel);
        });

        $element.remove();
    }

    private widthToPercent(width: number): number {
        return (100 / this.$canvas.width()) * width;
    }

    private heightToPercent(height: number): number {
        return (100 / this.$canvas.height()) * height;
    }

    public onResize() {
        let roomRatio = 1;
        switch (this.room.shape) {
            case RoomShape.Landscape:
                roomRatio = 0.7;
                break;
            case RoomShape.Portrait:
                roomRatio = 1.3;
                break;
        }

        let maxHeight = $(window).height() - 230;
        if (maxHeight < 200) maxHeight = 200;

        const maxWidth = this.$element.width() - this.$unplanned.width();
        let width,
            height,
            offsetLeft,
            offsetTop = 0;
        const windowRatio = maxHeight / maxWidth;

        if (windowRatio < roomRatio) {
            width = maxHeight / roomRatio;
            height = maxHeight;
            offsetLeft = (maxWidth - width) / 2;
            offsetTop = 0;
        } else {
            width = maxWidth;
            height = maxWidth * roomRatio;
            offsetLeft = 0;
            offsetTop = (maxHeight - height) / 2;
        }

        this.$element.height(maxHeight);
        this.$canvas.css({
            width: width,
            height: height,
            left: offsetLeft,
            top: offsetTop,
        });
    }

    public appendTo($element: JQuery): FloorPlanner {
        $element.append(this.$element);
        window.setTimeout(() => this.onResize(), 10);

        return this;
    }

    private minMaxDimension(m: { width: number; height: number; posX: number; posY: number }): {
        width: number;
        height: number;
        posX: number;
        posY: number;
    } {
        if (m.posX > 99) m.posX = 99;

        if (m.posY > 99) m.posY = 99;

        if (m.width > 100) m.width = 100;

        if (m.height > 100) m.height = 100;

        return m;
    }
}
